<template>
    <q-select filled v-model="administrator" use-input input-debounce="0" :label="label" :hint="hint"
        :options="administrators" @filter="filterAdministrator" @update:modelValue="selectAdministrator" :dense="true"
        :clearable="true" :loading="loading" :disable="disable">
        <template v-slot:no-option>
            <q-item>
                <q-item-section class="text-grey">
                    Sin resultados
                </q-item-section>
            </q-item>
        </template>
    </q-select>
</template>


<script lang="ts">
import { defineComponent, onMounted, reactive, toRefs, watch } from 'vue';
import { useQuasar } from 'quasar';

import { FindAdminAutocompleteDto, OptionDto } from '@/dtos';
import { errorNotify } from '@/helpers/utilities';
import administratorsService from '@/services/auth/administrators.service';

interface Props {
    clear?: boolean;
    label?: string;
    hint?: string;
    administrator_id?: number;
    is_responsable?: boolean;
    role_id?: string;
    company_id?: number;
    project_id?: number;
    disable?: boolean;
}

export default defineComponent({
    name: 'AdministratorAutocompleteComponent',
    props: {
        clear: { type: Boolean },
        administrator_id: { type: Number },
        label: { type: String },
        hint: { type: String },
        company_id: { type: Number },
        project_id: { type: Number },
        role_id: {
            type: String,
            default: '',
        },
        disable: {
            type: Boolean,
            default: false
        },
        is_responsable: {
            type: Boolean,
            default: false
        }
    },
    emits: ['administrator_selected'],
    setup(props: Props, { emit }) {
        const $q = useQuasar();

        const config = reactive({
            loading: false,
            label: props.label ?? 'Administrador',
            hint: props.hint,
            company_id: props.company_id,
            role_id: props.role_id,
            project_id: props.project_id,
            searcher: ''
        });

        const data = reactive({
            administrator: '',
            administrators: [] as OptionDto[],
            administrator_id: props.administrator_id,
        });

        const fetchAdministrators = async () => {
            try {
                config.loading = true;

                const params: FindAdminAutocompleteDto = {
                    searcher: config.searcher,
                    company_id: config.company_id,
                    role_id: config.role_id,
                    project_id: config.project_id
                }

                const administrators = await administratorsService.findAllAutocomplete(params);
                data.administrators = [];
                for (const admin of administrators.data) {
                    const opt: OptionDto = {
                        label: admin.first_name + " " + admin.last_name,
                        value: admin.id
                    }

                    data.administrators.push(opt);
                }

                if (data.administrator_id && data.administrator_id > 0) {
                    const admin = data.administrators.find(x => x.value == data.administrator_id);
                    if (admin) {
                        data.administrator = admin.label;
                        config.loading = false;
                        return;
                    }

                    await fetchAdministrator(data.administrator_id);
                }

                if (props.is_responsable) {
                    const opt: OptionDto = {
                        label: "Sin Responsable",
                        value: 0
                    }

                    data.administrators.push(opt);
                }

                config.loading = false;
            } catch (error) {
                console.log(error);
                config.loading = false;
                $q.notify(errorNotify('No se pudo obtener la información del listado de administradores'));
            }
        }

        const fetchAdministrator = async (administrator_id: number) => {
            try {
                const administratorResult = await administratorsService.findById(administrator_id);
                if (administratorResult.data) {
                    const opt: OptionDto = {
                        label: administratorResult.data.first_name + " " + administratorResult.data.last_name,
                        value: administratorResult.data.id
                    }

                    data.administrators.push(opt);
                    data.administrator = opt.label;
                }
            } catch (error) {
                console.log(error);
                $q.notify(errorNotify('No se pudo obtener la información del administrador'));
            }
        }

        const selectAdministrator = (option: OptionDto) => {
            emit('administrator_selected', option);
        }

        const filterAdministrator = (val: string, update: (fn: () => void) => void) => {
            if (val === '' && data.administrators.length == 0) {
                update(async () => {
                    config.searcher = '';
                    await fetchAdministrators();
                })
                return;
            }

            update(async () => {
                const needle = val.toLowerCase();
                if (needle.length > 2) {
                    config.searcher = needle;
                    data.administrator_id = undefined;
                    await fetchAdministrators();
                }
            })
        }

        onMounted(async () => {
            await fetchAdministrators();
        });

        watch([
            () => props.clear,
            () => props.administrator_id,
            () => props.company_id,
            () => props.role_id,
            () => props.project_id
        ],
            async ([newVal, newValId, newCompanyId, newRoleId, newProjectId], [oldVal, oldValId, oldCompanyId, oldRoleId, oldProjectId]) => {
                if (!newCompanyId && newCompanyId != oldCompanyId) {
                    config.company_id = undefined;
                    config.role_id = undefined;
                    data.administrator_id = undefined;

                    data.administrator = '';
                    await fetchAdministrators();
                    return;
                }

                if (newProjectId && newProjectId > 0 && newProjectId != oldProjectId) {
                    config.project_id = newProjectId;
                    data.administrator_id = undefined;

                    data.administrator = '';
                    await fetchAdministrators();
                    return;
                }

                if (newVal != oldVal) {
                    data.administrator = '';
                    data.administrator_id = undefined;

                    config.searcher = '';
                    await fetchAdministrators();
                    return;
                }

                if (newValId && newValId > 0) {
                    const administrator = data.administrators.find(x => x.value == newValId);
                    if (administrator) {
                        data.administrator = administrator.label;
                        return;
                    }

                    await fetchAdministrator(newValId);
                    return;
                }

                if (newCompanyId && newCompanyId > 0 && newCompanyId != oldCompanyId) {
                    config.company_id = newCompanyId;
                    data.administrator = '';
                }

                if (newRoleId && newRoleId != oldRoleId) {
                    config.role_id = newRoleId;
                    data.administrator = '';
                }

                if (newProjectId && newProjectId > 0 && newProjectId != oldProjectId) {
                    config.project_id = newProjectId;
                    data.administrator = '';
                }

                await fetchAdministrators();
            });

        return {
            ...toRefs(data),
            ...toRefs(config),
            filterAdministrator,
            selectAdministrator,
        }
    }
});
</script>
